import React from 'react';
import { X } from 'lucide-react';

const CreatePersonnelTab = ({
  selectedProjectLead, setSelectedProjectLead, projectLeads,
  selectedEstimator, setSelectedEstimator, estimators,
  estimatorAction, setEstimatorAction,
  selectedMarketingRep, setSelectedMarketingRep, marketingReps,
  selectedOfficeContact, setSelectedOfficeContact, officeContacts,
  officeAction, setOfficeAction,
  pmRows, setPmRows,
  // Production-Manager employees fetched live from /api/employees?role=Production
  // — matches what the Edit page (`DetailPersonnelTab`) uses, so the two
  // dropdowns stay in sync as employees are added/removed in Setup.
  productionEmployees = [],
  isDirty, onSave,
}) => {
  const handlePmChange = (index, field, value) => {
    setPmRows(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const handleAddPm = () => {
    setPmRows(prev => [...prev, { employee: '', percent: '100' }]);
  };

  const handleRemovePm = (index) => {
    setPmRows(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="relative">
      <div className="grid grid-cols-2 gap-8">
        {/* Project Personnel */}
        <div>
          <h2 className="text-2xl text-[#5B8DB8] font-light mb-6 border-l-4 border-[#5B8DB8] pl-4">Project Personnel</h2>
          
          <div className="space-y-6">
            <div>
              <label className="text-sm text-gray-600 block mb-2">Project Lead:</label>
              <select value={selectedProjectLead} onChange={(e) => setSelectedProjectLead(e.target.value)} className="border border-gray-300 rounded px-3 py-2 w-64">
                <option value="">-- select --</option>
                {projectLeads.map((emp, i) => (
                  <option key={i} value={`${emp.last_name}, ${emp.first_name}`}>{emp.last_name}, {emp.first_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-600 block mb-2">Estimator Contact</label>
              <div className="flex items-center gap-2">
                <select value={selectedEstimator} onChange={(e) => setSelectedEstimator(e.target.value)} className="border border-gray-300 rounded px-3 py-2 w-52">
                  <option value="">-- select --</option>
                  {estimators.map((emp, i) => (
                    <option key={i} value={`${emp.last_name}, ${emp.first_name}`}>{emp.last_name}, {emp.first_name}</option>
                  ))}
                </select>
                <label className="flex items-center gap-1 text-sm border border-gray-300 rounded px-2 py-2">
                  <input type="checkbox" checked={estimatorAction} onChange={(e) => setEstimatorAction(e.target.checked)} />
                  Action
                </label>
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-600 block mb-2">Marketing Rep:</label>
              <select value={selectedMarketingRep} onChange={(e) => setSelectedMarketingRep(e.target.value)} className="border border-gray-300 rounded px-3 py-2 w-64">
                <option value="">-- select --</option>
                {marketingReps.map((emp, i) => (
                  <option key={i} value={`${emp.last_name}, ${emp.first_name}`}>{emp.last_name}, {emp.first_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-600 block mb-2">Office Contact:</label>
              <div className="flex items-center gap-2">
                <select value={selectedOfficeContact} onChange={(e) => setSelectedOfficeContact(e.target.value)} className="border border-gray-300 rounded px-3 py-2 w-52">
                  <option value="">-- select --</option>
                  {officeContacts.map((emp, i) => (
                    <option key={i} value={`${emp.last_name}, ${emp.first_name}`}>{emp.last_name}, {emp.first_name}</option>
                  ))}
                </select>
                <label className="flex items-center gap-1 text-sm border border-gray-300 rounded px-2 py-2">
                  <input type="checkbox" checked={officeAction} onChange={(e) => setOfficeAction(e.target.checked)} />
                  Action
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Production Managers */}
        <div>
          <h2 className="text-2xl text-[#5B8DB8] font-light mb-6">Production Managers</h2>
          
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left p-2 text-gray-600 font-normal">Employee</th>
                <th className="text-left p-2 text-gray-600 font-normal">% Commission</th>
                <th className="text-left p-2 text-gray-600 font-normal">Action</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {pmRows.map((row, index) => (
                <tr key={index} className="border-t">
                  <td className="p-2">
                    <select value={row.employee} onChange={(e) => handlePmChange(index, 'employee', e.target.value)} className="border border-gray-300 rounded px-3 py-2 w-48" data-testid={`pm-employee-${index}`}>
                      <option value="">-- select --</option>
                      {productionEmployees.map((emp, i) => (
                        <option key={emp.id || i} value={`${emp.last_name}, ${emp.first_name}`}>
                          {emp.last_name}, {emp.first_name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="p-2">
                    <input
                      type="text"
                      value={row.percent}
                      onChange={(e) => handlePmChange(index, 'percent', e.target.value)}
                      className="border border-gray-300 rounded px-3 py-2 w-20"
                      data-testid={`pm-percent-${index}`}
                    />
                  </td>
                  <td className="p-2">
                    <input type="checkbox" className="w-4 h-4" data-testid={`pm-action-${index}`} />
                  </td>
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      {pmRows.length > 1 && (
                        <button onClick={() => handleRemovePm(index)} className="bg-[#DC3545] hover:bg-[#C82333] text-white rounded p-1.5" data-testid={`pm-remove-${index}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={onSave}
                        className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-3 py-1 rounded text-sm"
                        data-testid={`pm-save-${index}`}
                      >
                        Save
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          <div className="mt-4">
            <button onClick={handleAddPm} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-4 py-2 rounded text-sm" data-testid="add-pm-btn">
              Add PM
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePersonnelTab;
